import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../../config';
import settingsStore from '../../../store/settingsStore';
import {
  PHONE_STATUSES,
  DEFAULT_PHONE_STATUS,
  PHONE_CHANNELS,
  DEFAULT_PHONE_CHANNEL,
  PHONE_CHANNEL_LABELS,
} from '@claimup/shared/constants/phones';
import { getPhoneStatusLabel } from './phoneUtils';

const EditMaytapiPhone = ({ phone, isOpen, onClose }) => {
  const { updateMaytapiPhone } = settingsStore();
  const [name, setName] = useState('');
  const [status, setStatus] = useState(DEFAULT_PHONE_STATUS);
  const [canal, setCanal] = useState(DEFAULT_PHONE_CHANNEL);
  const [maxNewConversations, setMaxNewConversations] = useState('');
  const [maxActiveConversations, setMaxActiveConversations] = useState('');
  const [dailyColdLimit, setDailyColdLimit] = useState('');
  const [hourlyColdLimit, setHourlyColdLimit] = useState('');
  const [minGapMinutes, setMinGapMinutes] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${baseAPI}/users/list?role=company`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => setCompanies(res.data || []))
      .catch(() => setCompanies([]));
  }, [isOpen]);

  useEffect(() => {
    if (phone && isOpen) {
      setName(phone.name || '');
      setStatus(phone.status || DEFAULT_PHONE_STATUS);
      // Una ficha sin migrar llega SIN canal: el backend la lee con .lean() y
      // eso no aplica el defecto del esquema. El defecto de aquí la deja donde
      // estaba, en Maytapi, en vez de dejar el desplegable en blanco.
      setCanal(phone.canal || DEFAULT_PHONE_CHANNEL);
      setCompanyId(phone.company_id || '');
      setMaxNewConversations(phone.max_new_conversations === null || phone.max_new_conversations === undefined ? '' : phone.max_new_conversations);
      setMaxActiveConversations(phone.max_active_conversations === null || phone.max_active_conversations === undefined ? '' : phone.max_active_conversations);
      setDailyColdLimit(phone.daily_cold_limit === null || phone.daily_cold_limit === undefined ? '' : phone.daily_cold_limit);
      setHourlyColdLimit(phone.hourly_cold_limit === null || phone.hourly_cold_limit === undefined ? '' : phone.hourly_cold_limit);
      setMinGapMinutes(phone.min_gap_minutes === null || phone.min_gap_minutes === undefined ? '' : phone.min_gap_minutes);
    }
  }, [phone, isOpen]);

  const handleUpdatePhone = async () => {
    if (!name.trim()) {
      toast.error('Introduce un nombre');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const updateData = {
        name: name.trim(),
        status,
        canal,
        company_id: companyId, // '' → pool compartido (lo normaliza el backend)
      };

      if (maxNewConversations === '') {
        updateData.max_new_conversations = null;
      } else {
        const newLimit = parseInt(maxNewConversations, 10);
        if (isNaN(newLimit) || newLimit < 0) {
          toast.error('El límite de nuevas conversaciones debe ser un número válido');
          return;
        }
        updateData.max_new_conversations = newLimit;
      }

      if (maxActiveConversations === '') {
        updateData.max_active_conversations = null;
      } else {
        const activeLimit = parseInt(maxActiveConversations, 10);
        if (isNaN(activeLimit) || activeLimit < 0) {
          toast.error('El límite de conversaciones activas debe ser un número válido');
          return;
        }
        updateData.max_active_conversations = activeLimit;
      }

      // Límites del pool "en frío" (new + repetition). No nullables: solo se
      // envían si tienen un valor válido (si no, el backend mantiene el actual).
      if (dailyColdLimit !== '') {
        const v = parseInt(dailyColdLimit, 10);
        if (isNaN(v) || v < 0) {
          toast.error('El límite diario en frío debe ser un número válido');
          return;
        }
        updateData.daily_cold_limit = v;
      }
      if (hourlyColdLimit !== '') {
        const v = parseInt(hourlyColdLimit, 10);
        if (isNaN(v) || v < 1) {
          toast.error('El límite horario en frío debe ser ≥ 1');
          return;
        }
        updateData.hourly_cold_limit = v;
      }
      if (minGapMinutes !== '') {
        const v = parseInt(minGapMinutes, 10);
        if (isNaN(v) || v < 0) {
          toast.error('El intervalo mínimo (min_gap) debe ser un número válido');
          return;
        }
        updateData.min_gap_minutes = v;
      }

      const updateResponse = await axios.patch(
        `${baseAPI}/settings/phones/${phone._id}`,
        updateData,
        { headers }
      );
      toast.success('Teléfono actualizado correctamente');

      if (updateResponse?.data?.data) {
        updateMaytapiPhone(updateResponse.data.data);
      }

      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error updating Maytapi phone:', err);
      toast.error('Error al actualizar el teléfono');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !phone) {
    return null;
  }

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
        <div
          className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'
          onClick={onClose}
        />

        <div className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6'>
          <div className='sm:flex sm:items-start'>
            <div className='mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full'>
              <h3 className='text-lg font-semibold leading-6 text-gray-900'>Editar teléfono</h3>
              <div className='mt-4'>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Nombre</label>
                <input
                  type='text'
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder='Nombre descriptivo'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>Phone ID</label>
                <input
                  type='text'
                  value={phone.phone_id}
                  disabled
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-100 text-slate-500 sm:text-sm mb-4 cursor-not-allowed'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>Número de teléfono</label>
                <input
                  type='text'
                  value={phone.phone_number || ''}
                  disabled
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-100 text-slate-500 sm:text-sm mb-4 cursor-not-allowed'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>Estado</label>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm bg-white text-slate-900 mb-4'
                >
                  {PHONE_STATUSES.map(statusOption => (
                    <option key={statusOption} value={statusOption}>
                      {getPhoneStatusLabel(statusOption)}
                    </option>
                  ))}
                </select>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Canal</label>
                <select
                  value={canal}
                  onChange={e => setCanal(e.target.value)}
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm bg-white text-slate-900 mb-4'
                >
                  {PHONE_CHANNELS.map(opcion => (
                    <option key={opcion} value={opcion}>
                      {PHONE_CHANNEL_LABELS[opcion]}
                    </option>
                  ))}
                </select>
                <label className='block text-sm font-medium text-slate-700 mb-1'>Empresa con uso exclusivo</label>
                <select
                  value={companyId}
                  onChange={e => setCompanyId(e.target.value)}
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm bg-white text-slate-900 mb-4'
                >
                  <option value=''>Compartido (ClaimUp)</option>
                  {companies.map(c => (
                    <option key={c._id} value={c._id}>
                      {`${c.username || ''} ${c.userlastname || ''}`.trim() || c.usermail}
                    </option>
                  ))}
                </select>
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Máximo nuevas conversaciones por día (dejar vacío para ilimitado)
                </label>
                <input
                  type='number'
                  value={maxNewConversations}
                  onChange={e => setMaxNewConversations(e.target.value)}
                  placeholder='Ej: 50'
                  min='0'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Máximo conversaciones activas (dejar vacío para ilimitado)
                </label>
                <input
                  type='number'
                  value={maxActiveConversations}
                  onChange={e => setMaxActiveConversations(e.target.value)}
                  placeholder='Ej: 100'
                  min='0'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                />

                <div className='border-t border-slate-200 pt-4 mb-2'>
                  <p className='text-xs font-semibold uppercase text-slate-400'>Límites de envío en frío</p>
                  <p className='text-[11px] text-slate-400 mt-0.5'>
                    Mensajes de iniciación (nuevos + repeticiones). Las conversaciones ya respondidas no tienen límite.
                  </p>
                </div>
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Máximo en frío por día (sin contar bonus por respuestas)
                </label>
                <input
                  type='number'
                  value={dailyColdLimit}
                  onChange={e => setDailyColdLimit(e.target.value)}
                  placeholder='Ej: 6'
                  min='0'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Máximo en frío por hora
                </label>
                <input
                  type='number'
                  value={hourlyColdLimit}
                  onChange={e => setHourlyColdLimit(e.target.value)}
                  placeholder='Ej: 2'
                  min='1'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm mb-4'
                />
                <label className='block text-sm font-medium text-slate-700 mb-1'>
                  Intervalo mínimo entre envíos en frío (minutos)
                </label>
                <input
                  type='number'
                  value={minGapMinutes}
                  onChange={e => setMinGapMinutes(e.target.value)}
                  placeholder='Ej: 30'
                  min='0'
                  className='w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 sm:text-sm'
                />
              </div>
            </div>
          </div>

          <div className='mt-6 sm:mt-8 sm:flex sm:flex-row-reverse'>
            <button
              type='button'
              onClick={handleUpdatePhone}
              disabled={isSubmitting}
              className='inline-flex w-full justify-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type='button'
              onClick={onClose}
              className='mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto'
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMaytapiPhone;

